import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Plus } from "@untitledui/icons";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { Table, TableCard } from "@/components/application/table/table";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import {
  type LearningPathStatusChange,
  LearningPathStatusDialog,
} from "@/features/admin/learning-path-setup/components/LearningPathStatusDialog";
import {
  getPathStatusSummary,
  isPathArchived,
  isPathPublic,
  isPathPublished,
  isPathRestricted,
} from "@/features/admin/learning-path-setup/learningPathSetupStatus";
import { usePermissions } from "@/hooks/usePermissions";
import { useAdminListLearningPaths } from "@/services/api/admin-learningpaths/admin-learningpaths";
import { toNum } from "@/utils/course-formatters";

export const Route = createFileRoute("/admin/learning-paths/")({
  component: LearningPathsPage,
});

const formatDate = (value: string) => {
  try {
    return new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(value),
    );
  } catch {
    return value;
  }
};

const columns = [{}, {}, {}, {}] as object[];

/**
 * Every learning path with its Draft / Published / Restricted / Archived state. As for courses, a
 * published path is unpublished before it can be archived, and a restored path comes back as a
 * draft. Who can see a path (Restricted or everyone, LMS-154) is chosen while it's a draft.
 */
function LearningPathsPage() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission("Courses.Manage");
  const canPublish = hasPermission("Courses.Publish");
  const canAssign = hasPermission("LearningPaths.Assign");
  const [statusChange, setStatusChange] = useState<{
    pathId: string;
    change: LearningPathStatusChange;
  } | null>(null);
  const { data, isLoading, isError } = useAdminListLearningPaths({ query: { staleTime: 30_000 } });
  const paths = data ?? [];
  const featuredPath = paths.find((path) => path.isFeatured);

  return (
    // ProtectedRoute lets in anyone with the permission OR the role, so the role is one that has
    // Courses.Read: an Admin without it is turned away.
    <ProtectedRoute requiredPermissions={["Courses.Read"]} requiredRoles={["SuperAdmin"]}>
      <AdminPageLayout mainClassName="bg-secondary_alt">
        <div className="bg-secondary_alt pt-10 pb-16">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-4 sm:px-6 lg:px-8">
            <div className="border-secondary bg-primary flex flex-col gap-6 rounded-2xl border p-6 shadow-xs md:flex-row md:items-center md:justify-between">
              <div className="space-y-2">
                <p className="text-tertiary text-xs font-semibold tracking-[0.08em] uppercase">
                  Admin
                </p>
                <h1 className="text-primary text-3xl font-semibold">Learning paths</h1>
                <p className="text-tertiary max-w-xl text-sm">
                  Group related courses into a structured journey that learners follow in order.
                </p>
              </div>
              {canManage && (
                <div className="flex items-center gap-3 self-start md:self-center">
                  <Button
                    color="primary"
                    size="md"
                    iconLeading={Plus}
                    onClick={() => void navigate({ to: "/admin/learning-paths/new" })}
                  >
                    New learning path
                  </Button>
                </div>
              )}
            </div>

            <TableCard.Root className="border-secondary bg-primary overflow-hidden rounded-2xl border shadow-md">
              <TableCard.Header
                title="All learning paths"
                description="Most recently changed first."
              />

              <div className="relative min-h-[240px]">
                {isLoading && (
                  <div role="status" className="p-8 text-center text-sm text-gray-500">
                    Loading learning paths…
                  </div>
                )}

                {isError && (
                  <div
                    role="alert"
                    className="m-4 rounded border border-red-300 bg-red-50 p-3 text-red-800"
                  >
                    We couldn't load the learning paths. Reload the page to try again.
                  </div>
                )}

                {!isLoading && !isError && paths.length === 0 && (
                  <div className="p-8 text-center text-sm text-gray-500">
                    There are no learning paths yet.
                    {canManage ? " Create one to group related courses." : ""}
                  </div>
                )}

                {paths.length > 0 && (
                  <Table aria-label="Learning paths">
                    <Table.Header columns={columns}>
                      <Table.Head label="Learning path" isRowHeader />
                      <Table.Head label="Courses" />
                      <Table.Head label="Status" />
                      <Table.Head label="Actions" className="text-right" />
                    </Table.Header>
                    <Table.Body>
                      {paths.map((path) => {
                        const isPublished = isPathPublished(path);
                        const isArchived = isPathArchived(path);
                        const isRestricted = isPathRestricted(path);
                        // Only a public path has a page learners can open, or can be assigned or featured.
                        const isPublic = isPathPublic(path);
                        const status = getPathStatusSummary(path);
                        const statusAction: {
                          change: LearningPathStatusChange;
                          label: string;
                        } | null = isPublished
                          ? canPublish
                            ? { change: "unpublish", label: "Unpublish" }
                            : null
                          : !canManage
                            ? null
                            : isArchived
                              ? { change: "restore", label: "Restore" }
                              : { change: "archive", label: "Archive" };
                        // Only a published path can be featured; unpublishing or archiving one stops featuring it.
                        const featureAction: {
                          change: LearningPathStatusChange;
                          label: string;
                          ariaLabel: string;
                        } | null =
                          !canManage || !isPublic
                            ? null
                            : path.isFeatured
                              ? {
                                  change: "unfeature",
                                  label: "Unfeature",
                                  ariaLabel: `Stop featuring ${path.title}`,
                                }
                              : {
                                  change: "feature",
                                  label: "Feature",
                                  ariaLabel: `Feature ${path.title}`,
                                };
                        // Who can see a path is chosen while it's a draft (LMS-154).
                        const restrictionAction: {
                          change: LearningPathStatusChange;
                          label: string;
                          ariaLabel: string;
                        } | null =
                          !canPublish || isPublished || isArchived
                            ? null
                            : isRestricted
                              ? {
                                  change: "unrestrict",
                                  label: "Make public",
                                  ariaLabel: `Make ${path.title} public`,
                                }
                              : {
                                  change: "restrict",
                                  label: "Make Restricted",
                                  ariaLabel: `Make ${path.title} Restricted`,
                                };
                        const courseCount = toNum(path.courseCount);
                        return (
                          <Table.Row key={path.id} columns={columns}>
                            <Table.Cell>
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-semibold text-gray-900 md:text-base">
                                    {path.title}
                                  </span>
                                  {path.isFeatured && (
                                    <Badge color="brand" size="sm">
                                      Featured
                                    </Badge>
                                  )}
                                  {/* A live Restricted path says so in its status (LMS-154). */}
                                  {isRestricted && !isPublished && (
                                    <Badge color="brand" size="sm">
                                      Restricted
                                    </Badge>
                                  )}
                                </div>
                                <p className="line-clamp-2 text-xs text-gray-600 md:text-sm">
                                  {path.shortDescription}
                                </p>
                              </div>
                            </Table.Cell>
                            <Table.Cell>
                              <span className="text-sm text-gray-700">
                                {courseCount} course{courseCount === 1 ? "" : "s"}
                              </span>
                            </Table.Cell>
                            <Table.Cell>
                              <div className="flex flex-col gap-1 text-sm">
                                <Badge color={status.tone} size="sm">
                                  {status.label}
                                </Badge>
                                <span className="text-xs text-gray-500">
                                  Updated {formatDate(path.updatedAt)}
                                </span>
                              </div>
                            </Table.Cell>
                            <Table.Cell>
                              <div className="flex items-center justify-end gap-3">
                                {isPublic && (
                                  <Link
                                    to="/learning-paths/$pathId"
                                    params={{ pathId: path.id }}
                                    aria-label={`View ${path.title}`}
                                    className="text-arc-primary-900 hover:text-arc-gray-900 inline-flex min-h-11 items-center px-2 text-sm font-semibold underline underline-offset-4"
                                  >
                                    View
                                  </Link>
                                )}
                                {canAssign && (
                                  <Button
                                    color="secondary"
                                    size="sm"
                                    aria-label={`${isPublic ? "Assign" : "Assignments for"} ${path.title}`}
                                    onClick={() =>
                                      void navigate({
                                        to: "/admin/learning-paths/$pathId/assignments",
                                        params: { pathId: path.id },
                                      })
                                    }
                                  >
                                    {isPublic ? "Assign" : "Assignments"}
                                  </Button>
                                )}
                                {featureAction && (
                                  <Button
                                    color="secondary"
                                    size="sm"
                                    aria-label={featureAction.ariaLabel}
                                    onClick={() =>
                                      setStatusChange({
                                        pathId: path.id,
                                        change: featureAction.change,
                                      })
                                    }
                                  >
                                    {featureAction.label}
                                  </Button>
                                )}
                                {restrictionAction && (
                                  <Button
                                    color="secondary"
                                    size="sm"
                                    aria-label={restrictionAction.ariaLabel}
                                    onClick={() =>
                                      setStatusChange({
                                        pathId: path.id,
                                        change: restrictionAction.change,
                                      })
                                    }
                                  >
                                    {restrictionAction.label}
                                  </Button>
                                )}
                                {statusAction && (
                                  <Button
                                    color="secondary"
                                    size="sm"
                                    aria-label={`${statusAction.label} ${path.title}`}
                                    onClick={() =>
                                      setStatusChange({
                                        pathId: path.id,
                                        change: statusAction.change,
                                      })
                                    }
                                  >
                                    {statusAction.label}
                                  </Button>
                                )}
                                <Button
                                  color="secondary"
                                  size="sm"
                                  aria-label={`${canManage ? "Edit" : "Open"} ${path.title}`}
                                  onClick={() =>
                                    void navigate({
                                      to: "/admin/learning-paths/$pathId",
                                      params: { pathId: path.id },
                                    })
                                  }
                                >
                                  {canManage ? "Edit" : "Open"}
                                </Button>
                              </div>
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table>
                )}
              </div>
            </TableCard.Root>
          </div>
        </div>
        {statusChange && (
          <LearningPathStatusDialog
            pathId={statusChange.pathId}
            change={statusChange.change}
            isFeatured={featuredPath?.id === statusChange.pathId}
            replacesFeaturedTitle={
              featuredPath && featuredPath.id !== statusChange.pathId ? featuredPath.title : null
            }
            onClose={() => setStatusChange(null)}
          />
        )}
      </AdminPageLayout>
    </ProtectedRoute>
  );
}
